// Centre de notifications & tâches (WKF-013) — queries de lecture.
// Façade : ré-exporte types, mutations et erreurs pour un import unique.

import { useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'

import { entityHeaders, NOTIF_BASE, buildQuery, readError } from './useNotifications.shared'
import type {
    Notification,
    NotificationCalendar,
    NotificationList,
    NotificationListParams,
    NotificationTab,
    UnreadCount,
} from './useNotifications.types'

export * from './useNotifications.types'
export { notificationErrorMessage } from './useNotifications.errors'
export * from './useNotifications.mutations'

/** Compteurs de la cloche + par onglet. Rafraîchi périodiquement. */
export function useUnreadCount(entityId: string | null) {
    return useQuery<UnreadCount>({
        queryKey: queryKeys.notifications.unreadCount(entityId ?? ''),
        enabled: Boolean(entityId),
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        queryFn: async () => {
            const resp = await apiFetch(`${NOTIF_BASE}/unread-count`, {
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}

/** Détail d'une notification (dialogue de détail / édition, deep-link). */
export function useNotificationDetail(entityId: string | null, notificationId: string | null) {
    return useQuery<Notification>({
        queryKey: queryKeys.notifications.detail(notificationId ?? ''),
        enabled: Boolean(entityId) && Boolean(notificationId),
        queryFn: async () => {
            const resp = await apiFetch(`${NOTIF_BASE}/${notificationId}`, {
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}

/** Vue calendrier (WKF-013 §7.2bis) : notifications datées d'une plage `[from, to)`.
 *  Liste plate (pas de keyset — une plage mois/semaine est bornée). */
export function useNotificationCalendar(
    entityId: string | null,
    range: { from: string; to: string; tab?: NotificationTab } | null,
) {
    return useQuery<NotificationCalendar>({
        queryKey: queryKeys.notifications.calendar(
            entityId ?? '',
            range ? { from: range.from, to: range.to, tab: range.tab ?? 'tasks' } : undefined,
        ),
        enabled: Boolean(entityId) && Boolean(range),
        staleTime: 15_000,
        queryFn: async () => {
            const qs = buildQuery({
                date_from: range!.from,
                date_to: range!.to,
                tab: range!.tab ?? 'tasks',
            })
            const resp = await apiFetch(`${NOTIF_BASE}/calendar${qs}`, {
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}

/** Liste keyset des notifications (panneau cloche + page complète). */
export function useNotificationList(entityId: string | null, params: NotificationListParams = {}) {
    return useQuery<NotificationList>({
        queryKey: queryKeys.notifications.list(entityId ?? '', params as Record<string, unknown>),
        enabled: Boolean(entityId),
        staleTime: 15_000,
        queryFn: async () => {
            const qs = buildQuery({
                tab: params.tab,
                status: params.status,
                priority: params.priority,
                linked_entity_type: params.linked_entity_type,
                linked_entity_id: params.linked_entity_id,
                search: params.search,
                cursor: params.cursor ?? undefined,
                limit: params.limit,
                open_only: params.open_only ? 'true' : undefined,
                overdue: params.overdue ? 'true' : undefined,
            })
            const resp = await apiFetch(`${NOTIF_BASE}${qs}`, {
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })
}
